import React from 'react';
import {
  DollarSign,
  Package,
  Users,
  AlertTriangle,
  CheckCircle,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const AnalyticsTab = ({
  project,
  phases,
  orders,
  projectAnalytics,
  savingsData,
  waitlistStatus,
  joiningWaitlist,
  handleJoinWaitlist,
  completedPhases,
  pendingOrders,
  deliveredOrders,
  getTeamMember,
  getPhaseTimelineColor,
  formatCurrency,
  onOpenProductDetail,
}) => {
  return (
    <>
      {/* Summary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-zinc-200 p-4 ">
          <DollarSign className="w-7 h-7 text-primary mb-2" />
          <p className="text-2xl font-heading font-bold uppercase tracking-wide">{formatCurrency(project.total_budget)}</p>
          <p className="text-xs font-bold uppercase text-zinc-500">Total Budget</p>
        </div>
        <div className="bg-white border border-zinc-200 p-4 ">
          <DollarSign className="w-7 h-7 text-success mb-2" />
          <p className="text-2xl font-heading font-bold uppercase tracking-wide">{formatCurrency(project.spent_amount)}</p>
          <p className="text-xs font-bold uppercase text-zinc-500">Spent (Delivered)</p>
        </div>
        <div className="bg-white border border-zinc-200 p-4 ">
          <Package className="w-7 h-7 text-zinc-500 mb-2" />
          <p className="text-2xl font-heading font-bold uppercase tracking-wide">{orders.length}</p>
          <p className="text-xs font-bold uppercase text-zinc-500">Total Orders</p>
        </div>
        <div className="bg-white border border-zinc-200 p-4 ">
          <Users className="w-7 h-7 text-blue-500 mb-2" />
          <p className="text-2xl font-heading font-bold uppercase tracking-wide">{(project.assigned_users || []).length}</p>
          <p className="text-xs font-bold uppercase text-zinc-500">Team Members</p>
        </div>
      </div>

      {/* Budget Trajectory Prediction */}
      {projectAnalytics?.trajectory && (
        <div className={`border border-zinc-200 p-4 sm:p-6  mb-6 ${
          projectAnalytics.trajectory.direction === 'over' ? 'bg-red-50' : 'bg-green-50'
        }`} data-testid="budget-trajectory">
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 border border-zinc-200 flex items-center justify-center flex-shrink-0 ${
              projectAnalytics.trajectory.direction === 'over' ? 'bg-red-500' : 'bg-green-500'
            }`}>
              <AlertTriangle className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-lg sm:text-xl font-heading font-bold uppercase tracking-wide">
                At current pace, this project will finish approximately{' '}
                <span className={projectAnalytics.trajectory.direction === 'over' ? 'text-destructive' : 'text-success'}>
                  {formatCurrency(Math.abs(projectAnalytics.trajectory.variance))} {projectAnalytics.trajectory.direction} budget.
                </span>
              </p>
              <p className="text-sm text-zinc-500 mt-1">
                Spending {formatCurrency(projectAnalytics.trajectory.daily_spend_rate)}/day &middot;
                {' '}{projectAnalytics.trajectory.pct_timeline_elapsed}% timeline elapsed &middot;
                {' '}{projectAnalytics.trajectory.pct_budget_spent}% budget spent &middot;
                {' '}{projectAnalytics.trajectory.days_remaining} days remaining
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Burn Rate Chart */}
      {projectAnalytics?.burn_rate?.length > 0 && (
        <div className="bg-white border border-zinc-200 p-4 sm:p-6  mb-6" data-testid="burn-rate-chart">
          <h3 className="font-heading text-lg font-bold uppercase tracking-wide mb-4">Budget Burn Rate</h3>
          <p className="text-xs text-zinc-500 mb-3">Planned (dashed) vs. Actual (solid) cumulative spend</p>
          <div className="h-64 sm:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={projectAnalytics.burn_rate} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(d) => { try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); } catch { return d; } }}
                  tick={{ fontSize: 11, fontWeight: 700, fill: '#808080' }}
                  axisLine={{ stroke: '#000', strokeWidth: 2 }}
                />
                <YAxis
                  tickFormatter={(v) => `$${v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}`}
                  tick={{ fontSize: 11, fontWeight: 700, fill: '#808080' }}
                  axisLine={{ stroke: '#000', strokeWidth: 2 }}
                  width={55}
                />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    return (
                      <div className="bg-white border border-zinc-200 p-3 ">
                        <p className="text-xs font-bold mb-1">{label}</p>
                        {payload.map((p) => (
                          <p key={p.dataKey} className="text-sm" style={{ color: p.color }}>
                            <span className="font-heading font-bold uppercase tracking-wide">{p.dataKey === 'actual' ? 'Actual' : 'Planned'}:</span> {formatCurrency(p.value)}
                          </p>
                        ))}
                      </div>
                    );
                  }}
                />
                <Area type="monotone" dataKey="planned" stroke="#808080" strokeWidth={2} strokeDasharray="8 4" fill="none" dot={false} />
                <Area
                  type="monotone"
                  dataKey="actual"
                  stroke="#000"
                  strokeWidth={3}
                  fill={projectAnalytics.trajectory?.direction === 'over' ? 'rgba(239,68,68,0.15)' : 'rgba(34,197,94,0.15)'}
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Donut Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {projectAnalytics?.spend_by_phase?.length > 0 && (
          <div className="bg-white border border-zinc-200 p-4 sm:p-6 " data-testid="spend-by-phase-chart">
            <h3 className="font-heading text-lg font-bold uppercase tracking-wide mb-4">Spend by Phase</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={projectAnalytics.spend_by_phase} cx="50%" cy="50%" innerRadius="40%" outerRadius="70%" dataKey="value" nameKey="name" stroke="#000" strokeWidth={2}
                    label={({ name, value }) => `${name}: ${formatCurrency(value)}`}>
                    {projectAnalytics.spend_by_phase.map((slice, i) => (
                      <Cell key={slice.name || i} fill={['#FFC40C','#3B82F6','#22C55E','#F97316','#8B5CF6','#EC4899','#06B6D4','#78716C'][i % 8]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => formatCurrency(v)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
        {projectAnalytics?.spend_by_vendor?.length > 0 && (
          <div className="bg-white border border-zinc-200 p-4 sm:p-6 " data-testid="spend-by-vendor-chart">
            <h3 className="font-heading text-lg font-bold uppercase tracking-wide mb-4">Spend by Vendor</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={projectAnalytics.spend_by_vendor} cx="50%" cy="50%" innerRadius="40%" outerRadius="70%" dataKey="value" nameKey="name" stroke="#000" strokeWidth={2}
                    label={({ name, value }) => `${name}: ${formatCurrency(value)}`}>
                    {projectAnalytics.spend_by_vendor.map((slice, i) => (
                      <Cell key={slice.name || i} fill={['#1B3A5C','#FFC40C','#22C55E','#EF4444','#8B5CF6','#F97316','#06B6D4','#78716C'][i % 8]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => formatCurrency(v)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      {/* Cost Variance Table */}
      {projectAnalytics?.cost_variance?.length > 0 && (
        <div className="bg-white border border-zinc-200 p-4 sm:p-6  mb-6" data-testid="cost-variance-table">
          <h3 className="font-heading text-lg font-bold uppercase tracking-wide mb-4">Cost Variance by Phase</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b-2 border-black">
                  <th className="pb-3 pr-3 text-xs font-heading font-bold uppercase tracking-wide text-zinc-500">Phase</th>
                  <th className="pb-3 pr-3 text-xs font-heading font-bold uppercase tracking-wide text-zinc-500 text-right">Budgeted</th>
                  <th className="pb-3 pr-3 text-xs font-heading font-bold uppercase tracking-wide text-zinc-500 text-right">Actual</th>
                  <th className="pb-3 pr-3 text-xs font-heading font-bold uppercase tracking-wide text-zinc-500 text-right">Variance ($)</th>
                  <th className="pb-3 pr-3 text-xs font-heading font-bold uppercase tracking-wide text-zinc-500 text-right">Variance (%)</th>
                  <th className="pb-3 text-xs font-heading font-bold uppercase tracking-wide text-zinc-500 text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {projectAnalytics.cost_variance.map((cv) => (
                  <tr key={cv.phase_id} className="border-b border-gray-200 last:border-b-0" data-testid={`variance-row-${cv.phase_id}`}>
                    <td className="py-3 pr-3 font-bold text-sm">{cv.name}</td>
                    <td className="py-3 pr-3 text-right text-sm">{formatCurrency(cv.budgeted)}</td>
                    <td className="py-3 pr-3 text-right text-sm font-bold">{formatCurrency(cv.actual_spent)}</td>
                    <td className={`py-3 pr-3 text-right text-sm font-bold ${
                      cv.variance_dollar > 0 ? 'text-destructive' : cv.variance_dollar < 0 ? 'text-success' : ''
                    }`}>
                      {cv.variance_dollar > 0 ? '+' : ''}{formatCurrency(cv.variance_dollar)}
                    </td>
                    <td className={`py-3 pr-3 text-right text-sm font-bold ${
                      cv.variance_pct > 10 ? 'text-destructive' : cv.variance_pct < -10 ? 'text-success' : ''
                    }`}>
                      {cv.variance_pct > 0 ? '+' : ''}{cv.variance_pct}%
                    </td>
                    <td className="py-3 text-center">
                      <span className={`inline-block w-4 h-4 border border-black ${
                        cv.indicator === 'over' ? 'bg-red-500' :
                        cv.indicator === 'under' ? 'bg-green-500' : 'bg-primary'
                      }`} title={cv.indicator === 'over' ? 'Over Budget' : cv.indicator === 'under' ? 'Under Budget' : 'On Track'} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-200 text-xs text-zinc-500">
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-green-500 border border-black" /> Under Budget</span>
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-primary border border-black" /> On Track</span>
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-red-500 border border-black" /> Over Budget</span>
          </div>
        </div>
      )}

      {/* Top Materials + Price Efficiency */}
      {projectAnalytics && (projectAnalytics.top_materials?.length > 0 || projectAnalytics.price_efficiency) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          {/* Top Materials (2/3 width) */}
          <div className="lg:col-span-2 bg-white border border-zinc-200 p-4 sm:p-6" data-testid="top-materials-section">
            <h3 className="font-heading text-lg font-bold uppercase tracking-wide mb-3">Top Materials by Spend</h3>
            {projectAnalytics.top_materials?.length === 0 ? (
              <p className="text-sm text-zinc-500">Link line items to catalog products to populate this view.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[420px] text-sm">
                  <thead>
                    <tr className="border-b-2 border-black">
                      <th className="text-left text-[10px] font-heading font-bold uppercase tracking-wider text-zinc-500 px-2 py-2">Product</th>
                      <th className="text-right text-[10px] font-heading font-bold uppercase tracking-wider text-zinc-500 px-2 py-2">Qty</th>
                      <th className="text-right text-[10px] font-heading font-bold uppercase tracking-wider text-zinc-500 px-2 py-2">Spent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(projectAnalytics.top_materials || []).map((m) => (
                      <tr
                        key={m.product_id}
                        onClick={() => onOpenProductDetail && onOpenProductDetail(m.product_id)}
                        data-testid={`top-material-${m.product_id}`}
                        className={`border-b border-zinc-100 ${onOpenProductDetail ? 'hover:bg-yellow-50 cursor-pointer' : ''}`}
                      >
                        <td className="px-2 py-2">
                          <p className="font-medium text-zinc-900">{m.product_name}</p>
                          {m.category && (
                            <span className="inline-block mt-0.5 text-[10px] font-heading font-semibold uppercase tracking-wider px-1.5 py-0.5 bg-zinc-100 text-zinc-600">
                              {m.category}
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-2 text-right font-mono">{m.total_quantity}</td>
                        <td className="px-2 py-2 text-right font-mono font-bold">{formatCurrency(m.total_spent)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Price Efficiency (1/3 width) */}
          {projectAnalytics.price_efficiency && (
            <div className="bg-white border border-zinc-200 p-4 sm:p-6" data-testid="price-efficiency-section">
              <h3 className="font-heading text-lg font-bold uppercase tracking-wide mb-3">Price Efficiency</h3>
              {projectAnalytics.price_efficiency.efficiency_pct === null ? (
                <p className="text-sm text-zinc-500">Not enough linked line items to calculate.</p>
              ) : (
                <>
                  <p
                    data-testid="price-efficiency-score"
                    className={`font-mono text-5xl font-black tracking-tight ${
                      projectAnalytics.price_efficiency.efficiency_pct >= 70 ? 'text-emerald-600'
                      : projectAnalytics.price_efficiency.efficiency_pct >= 40 ? 'text-amber-600'
                      : 'text-red-600'
                    }`}
                  >
                    {projectAnalytics.price_efficiency.efficiency_pct}%
                  </p>
                  <p className="text-xs text-zinc-500 mt-2 leading-relaxed">
                    Of {projectAnalytics.price_efficiency.items_checked} catalog-linked item
                    {projectAnalytics.price_efficiency.items_checked !== 1 ? 's' : ''},{' '}
                    <span className="font-bold text-zinc-900">{projectAnalytics.price_efficiency.items_at_or_below_avg}</span> were
                    bought at or below your company's average price.
                  </p>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Potential Savings */}
      {savingsData && savingsData.items_above_average > 0 && (
        <div className="bg-white border border-zinc-200 p-4 sm:p-6  mb-6" data-testid="savings-analysis">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h3 className="font-heading text-lg font-bold uppercase tracking-wide">Potential Savings</h3>
              <p className="text-xs text-zinc-500 mt-1">Items where you paid above your company's historical average</p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-2xl font-heading font-bold uppercase tracking-wide text-success">{formatCurrency(savingsData.total_potential_savings)}</p>
              <p className="text-xs font-bold uppercase text-zinc-500">Est. Savings</p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b-2 border-black">
                  <th className="pb-3 pr-3 text-xs font-heading font-bold uppercase tracking-wide text-zinc-500">Product</th>
                  <th className="pb-3 pr-3 text-xs font-heading font-bold uppercase tracking-wide text-zinc-500">Vendor</th>
                  <th className="pb-3 pr-3 text-xs font-heading font-bold uppercase tracking-wide text-zinc-500 text-right">Paid</th>
                  <th className="pb-3 pr-3 text-xs font-heading font-bold uppercase tracking-wide text-zinc-500 text-right">Your Avg</th>
                  <th className="pb-3 text-xs font-heading font-bold uppercase tracking-wide text-zinc-500 text-right">Savings</th>
                </tr>
              </thead>
              <tbody>
                {savingsData.savings_items.slice(0, 10).map((item, idx) => (
                  <tr key={`${item.product_description}-${item.vendor_name}-${idx}`} className="border-b border-gray-200 last:border-b-0" data-testid={`savings-row-${idx}`}>
                    <td className="py-3 pr-3">
                      <p className="font-bold text-sm">{item.product_description}</p>
                      <p className="text-xs text-zinc-500">{item.quantity} {item.unit}</p>
                    </td>
                    <td className="py-3 pr-3 text-sm">{item.vendor_name}</td>
                    <td className="py-3 pr-3 text-right">
                      <span className="font-bold text-sm text-destructive">{formatCurrency(item.price_paid)}</span>
                      <p className="text-xs text-destructive">+{item.pct_above}%</p>
                    </td>
                    <td className="py-3 pr-3 text-right text-sm text-zinc-500">{formatCurrency(item.avg_price)}</td>
                    <td className="py-3 text-right font-heading font-bold uppercase tracking-wide text-sm text-success">{formatCurrency(item.total_savings)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {savingsData.savings_items.length > 10 && (
            <p className="text-xs text-zinc-500 mt-3">
              Showing top 10 of {savingsData.savings_items.length} items. Total potential savings: {formatCurrency(savingsData.total_potential_savings)}
            </p>
          )}
        </div>
      )}

      {/* Marketplace Teaser */}
      <div className="border-2 border-dashed border-gray-400 bg-zinc-50 p-5 sm:p-6 mb-6" data-testid="marketplace-teaser">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1">
            <h3 className="font-heading text-lg font-bold uppercase tracking-wide">Punchlist Marketplace — Coming Soon</h3>
            <p className="text-sm text-zinc-500 mt-1 leading-relaxed">
              Members save an average of 15-20% on materials through manufacturer-direct pricing.
            </p>
          </div>
          {waitlistStatus?.on_waitlist ? (
            <div className="flex items-center gap-2 px-4 py-2 bg-green-100 border-2 border-green-500 text-green-700 font-bold text-sm flex-shrink-0" data-testid="waitlist-confirmed">
              <CheckCircle className="w-4 h-4" />
              You're on the list!
            </div>
          ) : (
            <button
              onClick={handleJoinWaitlist}
              disabled={joiningWaitlist}
              data-testid="join-waitlist-btn"
              className="h-12 px-6 bg-primary text-zinc-900 font-bold uppercase border border-zinc-200  hover:bg-primary-hover active:translate-y-1 active: transition-all text-sm flex-shrink-0 disabled:opacity-50"
            >
              {joiningWaitlist ? 'Joining...' : 'Join the Waitlist'}
            </button>
          )}
        </div>
      </div>

      {/* Phase Progress + Order Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white border border-zinc-200 p-6 ">
          <h3 className="font-heading text-lg font-bold uppercase tracking-wide mb-4">Phase Progress</h3>
          {phases.length === 0 ? (
            <p className="text-zinc-500">No phases created</p>
          ) : (
            <div className="space-y-3">
              {phases.map((phase) => (
                <div key={phase.phase_id}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-medium">{phase.name}</span>
                    <span className={`font-bold ${
                      phase.status === 'Complete' ? 'text-success' :
                      phase.status === 'In Progress' ? 'text-blue-600' :
                      phase.status === 'Delayed' ? 'text-destructive' : 'text-zinc-500'
                    }`}>{phase.status}</span>
                  </div>
                  <div className="h-3 bg-zinc-100 border border-black overflow-hidden">
                    <div
                      className={`h-full ${getPhaseTimelineColor(phase.status)}`}
                      style={{ width: phase.status === 'Complete' ? '100%' : phase.status === 'In Progress' ? '50%' : '0%' }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="mt-4 pt-4 border-t-2 border-black">
            <p className="text-sm">
              <span className="font-bold text-lg">{completedPhases}</span> of <span className="font-bold text-lg">{phases.length}</span> phases complete
            </p>
          </div>
        </div>

        <div className="bg-white border border-zinc-200 p-6 ">
          <h3 className="font-heading text-lg font-bold uppercase tracking-wide mb-4">Order Summary</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-zinc-500">Subtotal (all orders)</span>
              <span className="font-bold">{formatCurrency(orders.reduce((s, o) => s + o.subtotal, 0))}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">Total Tax</span>
              <span className="font-bold">{formatCurrency(orders.reduce((s, o) => s + o.tax, 0))}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">Total Freight</span>
              <span className="font-bold">{formatCurrency(orders.reduce((s, o) => s + o.freight, 0))}</span>
            </div>
            <div className="flex justify-between pt-3 border-t-2 border-black">
              <span className="font-bold">Grand Total</span>
              <span className="font-heading font-bold uppercase tracking-wide text-lg">{formatCurrency(orders.reduce((s, o) => s + o.total, 0))}</span>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t-2 border-black">
            <div className="flex justify-between text-sm">
              <span><span className="font-bold">{pendingOrders}</span> pending</span>
              <span><span className="font-bold text-success">{deliveredOrders}</span> delivered</span>
            </div>
          </div>
        </div>
      </div>

      {(project.assigned_users || []).length > 0 && (
        <div className="bg-white border border-zinc-200 p-6 ">
          <h3 className="font-heading text-lg font-bold uppercase tracking-wide mb-4">Assigned Team</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {(project.assigned_users || []).map(userId => {
              const member = getTeamMember(userId);
              if (!member) return null;
              return (
                <div key={userId} className="flex items-center gap-3 p-3 border border-zinc-200 bg-zinc-50" data-testid={`analytics-team-${userId}`}>
                  {member.picture ? (
                    <img src={member.picture} alt={member.name} className="w-10 h-10 rounded-full border border-zinc-200 object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full border border-zinc-200 bg-zinc-700 flex items-center justify-center">
                      <span className="text-white text-sm font-bold">{member.name?.charAt(0)?.toUpperCase()}</span>
                    </div>
                  )}
                  <div>
                    <p className="font-bold text-sm">{member.name}</p>
                    <p className="text-xs text-zinc-500">{member.role}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};

export default AnalyticsTab;
